import { BlobServiceClient } from '@azure/storage-blob';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { HouseholdData } from './pages/household/types';
import {
  AllTeamOdds,
  AllTeamOddsWC26,
  BracketFixture,
  BracketStanding,
  Fixture,
  GroupWinner,
  MatchStats,
  ScoringRules,
  TopScorer,
  UserTeams,
} from './types';

const sweepstakesAccount = process.env.REACT_APP_SWEEPSTAKES_STORAGE_ACCOUNT ?? '';
const sweepstakesSasToken = process.env.REACT_APP_SWEEPSTAKES_STORAGE_SAS ?? '';
const sweepstakesContainerName = process.env.REACT_APP_SWEEPSTAKES_STORAGE_CONTATINER ?? '';

const blobServiceClient = new BlobServiceClient(`https://${sweepstakesAccount}.blob.core.windows.net/?${sweepstakesSasToken}`);
const containerClient = blobServiceClient.getContainerClient(sweepstakesContainerName);

// --- Blob path constants ---

const EURO2024 = {
  scoring: 'scoring.json',
  teamAssignments: 'teamAssignments.json',
  preTournamentOdds: 'preTournamentOdds.json',
  topScorers: 'topScorers.json',
  groupWinners: 'groupWinners.json',
  fixtures: (date: string) => `fixtures/${date}.json`,
  allFixtures: 'fixtures/',
  matchStats: (id: string) => `matchStats/${id}.json`,
  allMatchStats: 'matchStats/',
};

const WC26 = {
  scoring: 'wc26/scoring.json',
  teamAssignments: 'wc26/teamAssignments.json',
  preTournamentOdds: 'wc26/preTournamentOdds.json',
  topScorers: 'wc26/topScorers.json',
  groupWinners: 'wc26/groupWinners.json',
  fixtures: (date: string) => `wc26/fixtures/${date}.json`,
  matchStats: (id: string) => `wc26/matchStats/${id}.json`,
  allMatchStats: 'wc26/matchStats/',
  directFkGoals: 'wc26/directFkGoals.json',
  olimpicoGoals: 'wc26/olimpicoGoals.json',
  keeperGoals: 'wc26/keeperGoals.json',
  goalSeconds: 'wc26/goalSeconds.json',
  quotaStatus: 'wc26/quotaStatus.json',
  bracketFixtures: 'wc26/bracket/fixtures.json',
  bracketStandings: 'wc26/bracket/standings.json',
};

// --- Hooks ---

const useBlobData = <T>(
  blobName: string,
  refreshIntervalMs?: number
): [boolean, T | undefined, boolean] => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<boolean>(false);

  const blobFetcher = async (): Promise<any> => {
    setLoading(true);
    setError(false);
    try {
      const blobClient = containerClient.getBlockBlobClient(blobName);
      const downloadBlockBlobResponse = await blobClient.download(0);
      const body = await downloadBlockBlobResponse.blobBody;
      const text = await body?.text();
      return JSON.parse(text ?? '');
    } catch (err: any) {
      if (err?.statusCode === 404) {
        // eslint-disable-next-line no-console
        console.warn(`Blob not found (no data yet): ${blobName}`);
        return null;
      }
      // eslint-disable-next-line no-console
      console.error(`Error fetching blob: ${blobName}`, err);
      setError(true);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const blobData = await blobFetcher();
      setData(blobData);
    };

    fetchData();

    if (!refreshIntervalMs) return undefined;
    const interval = setInterval(fetchData, refreshIntervalMs);
    return () => clearInterval(interval);
  }, [blobName, refreshIntervalMs]);

  return [loading, data, error];
};

const useBlobFolder = <T>(folderName: string,
  refreshIntervalMs?: number): [boolean, T[], boolean] => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<T[]>([]);
  const [error, setError] = useState<boolean>(false);

  const fetchBlobsInFolder = async (): Promise<T[]> => {
    setLoading(true);
    setError(false);
    try {
      const blobDataArray: T[] = [];
      for await (const blob of containerClient.listBlobsFlat({ prefix: folderName })) {
        const blobClient = containerClient.getBlockBlobClient(blob.name);
        const downloadBlockBlobResponse = await blobClient.download(0);
        const body = await downloadBlockBlobResponse.blobBody;
        const text = await body?.text();
        if (text) {
          blobDataArray.push(JSON.parse(text) as T);
        }
      }
      return blobDataArray;
    } catch (err: any) {
      if (err?.statusCode !== 404) {
        // eslint-disable-next-line no-console
        console.error(`Error fetching blobs in folder: ${folderName}`, err);
        setError(true);
      } else {
        // eslint-disable-next-line no-console
        console.warn(`Blob folder not found (no data yet): ${folderName}`);
      }
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const blobsData = await fetchBlobsInFolder();
      setData(blobsData);
    };

    fetchData();

    if (!refreshIntervalMs) return undefined;
    const interval = setInterval(fetchData, refreshIntervalMs);
    return () => clearInterval(interval);
  }, [folderName, refreshIntervalMs]);

  return [loading, data, error];
};

// --- Euro 2024 queries ---

export const useScoringRules = () => useBlobData<ScoringRules>(EURO2024.scoring);
export const useUserTeams = () => useBlobData<UserTeams[]>(EURO2024.teamAssignments);
export const usePreTournamentOdds = () => useBlobData<AllTeamOdds>(EURO2024.preTournamentOdds);
export const useTopScorers = () => useBlobData<TopScorer[]>(EURO2024.topScorers);
export const useGroupWinners = () => useBlobData<GroupWinner[]>(EURO2024.groupWinners);
export const useMatchStats = (matchId: string) => useBlobData<MatchStats>(
  EURO2024.matchStats(matchId)
);

export const useTodaysFixtures = (date: string): [boolean, Fixture[] | undefined, boolean] => {
  const [loading, fixtures, error] = useBlobData<Fixture[]>(EURO2024.fixtures(date));
  return [
    loading,
    fixtures?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    error
  ];
};

export const useAllFixtures = (): [boolean, Fixture[], boolean] => {
  const [loading, fixtures, error] = useBlobFolder<Fixture[]>(EURO2024.allFixtures);
  const flat = useMemo(() => fixtures.flat(), [fixtures]);
  return [loading, flat, error];
};

export const useAllMatchStats = (): [boolean, MatchStats[], boolean] => {
  const [loading, matchStats, error] = useBlobFolder<MatchStats[]>(EURO2024.allMatchStats);
  const flat = useMemo(() => matchStats.flat(), [matchStats]);
  return [loading, flat, error];
};

// --- WC26 queries ---

export const useWC26ScoringRules = () => useBlobData<ScoringRules>(WC26.scoring);
export const useWC26UserTeams = () => useBlobData<UserTeams[]>(WC26.teamAssignments);
export const useWC26PreTournamentOdds = () => useBlobData<AllTeamOddsWC26>(WC26.preTournamentOdds);
export const useTopScorersWC26 = () => useBlobData<TopScorer[]>(WC26.topScorers);
export const useGroupWinnersWC26 = () => useBlobData<GroupWinner[]>(WC26.groupWinners);
// eslint-disable-next-line max-len
export const useMatchStatsWC26 = (matchId: string, refreshIntervalMs?: number) => useBlobData<MatchStats>(
  WC26.matchStats(matchId),
  refreshIntervalMs
);

export const useTodaysFixturesWC26 = (date: string): [boolean, Fixture[] | undefined, boolean] => {
  const [loading, fixtures, error] = useBlobData<Fixture[]>(WC26.fixtures(date));
  return [
    loading,
    fixtures?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    error
  ];
};

const WC26_MATCH_STATS_REFRESH_MS = 60_000;

export const useAllMatchStatsWC26 = (): [boolean, MatchStats[], boolean] => {
  const [loading, matchStats, error] = useBlobFolder<MatchStats[]>(
    WC26.allMatchStats,
    WC26_MATCH_STATS_REFRESH_MS
  );
  const flat = useMemo(() => matchStats.flat(), [matchStats]);
  return [loading, flat, error];
};

export const useDirectFkGoals = () => useBlobData<string[]>(WC26.directFkGoals);
export const useOlimpicoGoals = () => useBlobData<string[]>(WC26.olimpicoGoals);
export const useKeeperGoals = () => useBlobData<string[]>(WC26.keeperGoals);
export const useBracketFixtures = () => useBlobData<BracketFixture[]>(WC26.bracketFixtures);
export const useBracketStandings = () => useBlobData<BracketStanding[]>(WC26.bracketStandings);

const saveGoalKeys = async (blobName: string, goalKeys: string[]): Promise<void> => {
  const blobClient = containerClient.getBlockBlobClient(blobName);
  const content = JSON.stringify(goalKeys);
  await blobClient.upload(content, content.length, {
    blobHTTPHeaders: { blobContentType: 'application/json' },
    conditions: {},
  });
};

export const saveDirectFkGoals = (goalKeys: string[]) => saveGoalKeys(WC26.directFkGoals, goalKeys);
export const saveOlimpicoGoals = (goalKeys: string[]) => saveGoalKeys(WC26.olimpicoGoals, goalKeys);
export const saveKeeperGoals = (goalKeys: string[]) => saveGoalKeys(WC26.keeperGoals, goalKeys);

export const useGoalSeconds = () => useBlobData<Record<string, number>>(WC26.goalSeconds);

export type QuotaStatus = {
  limit: number;
  remaining: number;
  resetInSeconds: number;
  timestamp: string;
};

export const useQuotaStatus = () => useBlobData<QuotaStatus>(WC26.quotaStatus);

export const saveGoalSeconds = async (secondsMap: Record<string, number>): Promise<void> => {
  const blobClient = containerClient.getBlockBlobClient(WC26.goalSeconds);
  const content = JSON.stringify(secondsMap);
  await blobClient.upload(content, content.length, {
    blobHTTPHeaders: { blobContentType: 'application/json' },
    conditions: {},
  });
};

// --- Household task board ---

const HOUSEHOLD = {
  data: 'household/data.json',
};

export const EMPTY_HOUSEHOLD_DATA: HouseholdData = { projects: [], items: [], completions: [] };

export const useHouseholdData = (): [
  boolean,
  HouseholdData,
  boolean,
  () => Promise<void>
] => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<HouseholdData>(EMPTY_HOUSEHOLD_DATA);
  const [error, setError] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const blobClient = containerClient.getBlockBlobClient(HOUSEHOLD.data);
      const downloadResponse = await blobClient.download(0);
      const body = await downloadResponse.blobBody;
      const text = await body?.text();
      setData(text ? { ...EMPTY_HOUSEHOLD_DATA, ...JSON.parse(text) } : EMPTY_HOUSEHOLD_DATA);
    } catch (err: any) {
      if (err?.statusCode === 404) {
        setData(EMPTY_HOUSEHOLD_DATA);
      } else {
        // eslint-disable-next-line no-console
        console.error('Error fetching household data', err);
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return [loading, data, error, fetchData];
};

export const saveHouseholdData = async (data: HouseholdData): Promise<void> => {
  const blobClient = containerClient.getBlockBlobClient(HOUSEHOLD.data);
  const content = JSON.stringify(data);
  await blobClient.upload(content, content.length, {
    blobHTTPHeaders: { blobContentType: 'application/json' },
    conditions: {},
  });
};
